import '../sass/components/product-search.scss'

import Selects from "./Selects";
import React, {useContext, useEffect, useRef, useState} from "react";
import ProductList from "./ProductList";
import {fetchSearchOptions, fetchAutocompleteSuggestions, warmAutocompleteCache} from "../apiRequests/api";
import {useAuth0} from "@auth0/auth0-react";
import {filterFields} from "../settings";
import {SigninContext} from "../contexts/SigninContext";
import Loading from "./Loading";
import {useBasePath} from "../hooks/useBasePath";

const AUTOCOMPLETE_DEBOUNCE_MS = 500;
const AUTOCOMPLETE_MIN_CHARS = 2;


function ProductSearch() {
    const [selectsLoading, setSelectsLoading] = useState(true)
    const {userName} = useContext(SigninContext)
    const basePath = useBasePath()
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [filterStates, setFilterStates] = useState(filterFields.map(el => ''))
    const [filterInfo, setFilterInfo] = useState(filterFields.map(el => el))
    const [suggestions, setSuggestions] = useState([])
    const [suggestionsVisible, setSuggestionsVisible] = useState(false)
    const [suggestionsWarming, setSuggestionsWarming] = useState(false)
    const suggestionsDebounceRef = useRef(null)
    const [fetchParameters, setFetchParameters] = useState(
        {
            "productsSearchParameters": {
                'description': '',
                'colour': '',
                'organic/biodynamic': '',
                'closure': '',
                'country': '',
                'supergroup': 'Wines',
                'productgroup': '',
            }
        }
    )
    const simulateClick = React.useRef(null)

    // equalize fetch parameters to states of search filters

    function searchProducts(overrideFilterStates) {
        const states = overrideFilterStates || filterStates;
        setIsSubmitted(true);
        let fetchObj = {"productsSearchParameters": {}};

        filterFields.forEach((el, index) => {
            fetchObj.productsSearchParameters[el.name] = states[index];
        });
        setFetchParameters(fetchObj);

        // Store filter states in sessionStorage
        sessionStorage.setItem("storedFilterStates", JSON.stringify(states));
    }

    function clearSearch() {
        setFilterStates(filterFields.map(el => ''));
        setFetchParameters({"productsSearchParameters": {}});
        clearStoredFilters();
        setIsSubmitted(false);
    }


    function clearStoredFilters() {
        // Clear stored filter states from sessionStorage
        sessionStorage.removeItem("storedFilterStates");
    }

    const {getIdTokenClaims} = useAuth0()

    // we need to request options first from api to be rendered for search selects
    async function loadOptions() {
        const token = await getIdTokenClaims()
        const data = await fetchSearchOptions(token.__raw)
        if (!data.error) {
            setFilterInfo(filterInfo.map(state => {
                if (state.select) {
                    state.options = data.mainResult[state.name]
                    return state
                } else {
                    return state
                }
            }))
            setSelectsLoading(false)
            if (!window.location.href.endsWith('/product-search/1/20') || JSON.stringify(sessionStorage.getItem("storedFilterStates")).length > 28) {
                simulateClick.current.click()
            }
        }
    }

    function handleFilterChange(val, order) {
        const newArr = filterStates
        newArr[order] = val
        setFilterStates(filterStates.map((el, index) => index === order ? val : el))
    }

    function handleKeyDown(event) {
        if (event.key === 'Enter') {
            searchProducts();
        }
    }

    async function fetchSuggestions(keyword) {
        const token = await getIdTokenClaims()
        const data = await fetchAutocompleteSuggestions({
            keyword,
            basePath,
            CustomerAccount: userName,
        }, token.__raw)

        if (data.error) {
            return
        }
        const results = data.mainResult || []
        setSuggestionsWarming(!!data.warming)
        setSuggestions(results)
        setSuggestionsVisible(!!data.warming || results.length > 0)
    }

    function handleTitleInputChange(value) {
        handleFilterChange(value, 0)

        clearTimeout(suggestionsDebounceRef.current)
        const keyword = value.trim()
        if (!userName || keyword.length < AUTOCOMPLETE_MIN_CHARS) {
            setSuggestions([])
            setSuggestionsVisible(false)
            return
        }

        suggestionsDebounceRef.current = setTimeout(() => fetchSuggestions(keyword), AUTOCOMPLETE_DEBOUNCE_MS)
    }

    // Selecting a suggestion searches by that exact title. Other facet
    // filters are reset because suggestions are drawn from the whole
    // catalogue, keeping a stale facet (e.g. colour) selected could hide the
    // very product the user just picked.
    function handleSuggestionClick(title) {
        const newFilterStates = filterFields.map(field => field.name === 'description' ? title : '')
        setFilterStates(newFilterStates)
        setSuggestionsVisible(false)
        searchProducts(newFilterStates)
    }

    useEffect(() => {
        loadOptions();

        // Restore filter states from sessionStorage
        const storedFilterStates = sessionStorage.getItem("storedFilterStates");
        if (storedFilterStates) {
            setFilterStates(JSON.parse(storedFilterStates));
        }

    }, []);

    // Warm the "all products for this customer" cache as soon as we know
    // who the customer is, so it's ready by the time they start typing.
    useEffect(() => {
        if (!userName) {
            return
        }
        (async () => {
            const token = await getIdTokenClaims()
            warmAutocompleteCache({basePath, CustomerAccount: userName}, token.__raw)
        })()
    }, [userName]);

    return (
        <div className="product-search-page">
            <h2 className='product-search-page-title'>Product Search</h2>
            <Loading isLoading={selectsLoading}/>
            <div className="product-search-filters">
                {!selectsLoading &&
                    filterFields.map((field, index) => (
                        field.select ? (
                            <div key={index} className="product-search-tool product-search-select">
                                <p>{field.title}</p>
                                <Selects options={filterInfo[index].options} setPropsValue={handleFilterChange}
                                         value={filterStates[index]} order={index}/>
                            </div>
                        ) : field.name === 'description' ? (
                            <div key={index} className="product-search-tool product-search-autocomplete">
                                <p>{field.title}</p>
                                <input type={"text"}
                                       value={filterStates[index]}
                                       name={field.name}
                                       autoComplete={"off"}
                                       onChange={(e) => handleTitleInputChange(e.target.value)}
                                       onKeyDown={handleKeyDown}
                                       onFocus={() => setSuggestionsVisible(suggestions.length > 0)}
                                       onBlur={() => setSuggestionsVisible(false)}
                                />
                                {suggestionsVisible &&
                                  <ul className="product-search-suggestions">
                                      {suggestionsWarming &&
                                        <li className="product-search-suggestions-status">Still loading the full product list, one moment…</li>
                                      }
                                      {suggestions.map((suggestion) => (
                                          <li key={suggestion.Code}
                                              onMouseDown={(e) => e.preventDefault()}
                                              onClick={() => handleSuggestionClick(suggestion.Title)}>
                                              {suggestion.Title}
                                          </li>
                                      ))}
                                  </ul>
                                }
                            </div>
                        ) : (
                            <div key={index} className="product-search-tool">
                                <p>{field.title}</p>
                                <input type={"text"}
                                       value={filterStates[index]}
                                       name={field.name}
                                       onChange={(e) => handleFilterChange(e.target.value, index)}
                                       onKeyDown={handleKeyDown}
                                />
                            </div>
                        )
                    ))
                }
                {!selectsLoading &&
                  <div className="product-search-actions">
                      <button ref={simulateClick}
                              onClick={() => clearSearch()}
                              className="product-search-tool product-clear-button">Clear
                      </button>
                      <button ref={simulateClick}
                              onClick={() => searchProducts()}
                              className="product-search-tool product-search-button">Search
                      </button>
                  </div>
                }
            </div>
            {isSubmitted && <ProductList fetchParams={fetchParameters}/>}
        </div>
    )
}

export default ProductSearch